import { useEffect, useRef, useCallback, useState } from "react"
import { useLocation } from "@tanstack/react-router"
import { useAuthState } from "react-firebase-hooks/auth"
import { logEvent, setUserId } from "firebase/analytics"
import { auth, getAnalyticsInstance } from "@/lib/firebase"
import { useTenant } from "@/helpers/firebaseGetMethods"

const getSessionId = () => {
  if (globalThis.window === undefined) return null

  // sessionStorage throws in some restricted contexts (private browsing,
  // sandboxed iframes). Fall back to an in-memory ID rather than crashing.
  try {
    let sessionId = sessionStorage.getItem("analytics_session_id")
    if (!sessionId) {
      sessionId = `${Date.now()}-${crypto.randomUUID()}`
      sessionStorage.setItem("analytics_session_id", sessionId)
    }
    return sessionId
  } catch {
    return `${Date.now()}-${crypto.randomUUID()}`
  }
}

const getDeviceType = () => {
  if (globalThis.window === undefined) return "unknown"
  return window.innerWidth < 768 ? "mobile" : "desktop"
}

export const usePageAnalytics = () => {
  const pathname = useLocation({ select: (loc) => loc.pathname })
  const [user, authLoading] = useAuthState(auth)
  const { data: tenantData, isLoading: tenantLoading } = useTenant({ enabled: !!user })

  // Use state (not a ref) so the tracking effect re-runs once analytics is ready;
  // otherwise the first page_view fires while analyticsRef is still null and is lost.
  const [analytics, setAnalytics] = useState(null)
  const pageOpenTime = useRef(null)
  const currentPath = useRef(null)
  const sessionIdRef = useRef(null)
  const hasLoggedPageView = useRef(false)

  // Capture session ID once on mount
  useEffect(() => {
    sessionIdRef.current = getSessionId()
  }, [])

  // Initialize analytics instance and set Firebase user ID
  useEffect(() => {
    let cancelled = false
    getAnalyticsInstance()
      .then((instance) => {
        if (cancelled || !instance) return
        setAnalytics(instance)
        if (user?.uid) setUserId(instance, user.uid)
      })
      .catch((err) => {
        // Analytics is non-critical; log in dev and otherwise stay silent
        // so a failed analytics init never breaks the page.
        if (import.meta.env.DEV) console.warn("Analytics init failed:", err)
      })
    return () => {
      cancelled = true
    }
  }, [user?.uid])

  const tenant = tenantData?.userData?.tenant || "unknown"
  const isInternal = tenantData?.userData?.isInternal || false

  const getUserData = useCallback(
    () => ({
      user_id: user?.uid || "anonymous",
      tenant_id: tenant,
      is_authenticated: !!user?.uid,
      isInternal
    }),
    [user?.uid, tenant, isInternal]
  )

  // Build common event params — use pathname only to avoid logging sensitive query params
  const buildEventParams = useCallback(
    (extraParams) => ({
      ...getUserData(),
      page_path: currentPath.current || pathname,
      page_title: document.title,
      session_id: sessionIdRef.current,
      ...extraParams
    }),
    [getUserData, pathname]
  )

  // Stash the latest callbacks in refs so the route-tracking effect below
  // only re-runs on real route/auth changes — without this, a tenantData
  // resolution flips getUserData identity and the effect emits a spurious
  // page_exit + page_view_custom pair for the same pathname.
  const getUserDataRef = useRef(getUserData)
  const buildEventParamsRef = useRef(buildEventParams)
  useEffect(() => {
    getUserDataRef.current = getUserData
    buildEventParamsRef.current = buildEventParams
  }, [getUserData, buildEventParams])

  useEffect(() => {
    // Only track authenticated users with a resolved tenant and a live analytics instance
    if (authLoading || tenantLoading || !user || !analytics) return

    const trackPageView = () => {
      const now = Date.now()
      pageOpenTime.current = now
      currentPath.current = pathname
      hasLoggedPageView.current = true

      logEvent(analytics, "page_view_custom", {
        ...getUserDataRef.current(),
        page_path: pathname,
        page_title: document.title,
        session_id: sessionIdRef.current,
        device_type: getDeviceType(),
        referrer: document.referrer || "direct",
        timestamp_opened: new Date(now).toISOString()
      })
    }

    const trackPageExit = () => {
      if (!pageOpenTime.current) return

      const duration = Date.now() - pageOpenTime.current

      logEvent(
        analytics,
        "page_exit",
        buildEventParamsRef.current({
          duration_ms: duration,
          duration_seconds: Math.round(duration / 1000)
        })
      )

      // Clear timing state so a subsequent visibility-hidden or unmount cleanup
      // doesn't log a second page_exit for the same open interval.
      pageOpenTime.current = null
      hasLoggedPageView.current = false
    }

    trackPageView()

    const trackPageRevisit = () => {
      const now = Date.now()
      pageOpenTime.current = now
      hasLoggedPageView.current = true
      logEvent(analytics, "page_revisit", {
        ...getUserDataRef.current(),
        page_path: pathname,
        page_title: document.title,
        session_id: sessionIdRef.current,
        timestamp_opened: new Date(now).toISOString()
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        trackPageExit()
      } else if (document.visibilityState === "visible") {
        trackPageRevisit()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      if (hasLoggedPageView.current) {
        trackPageExit()
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [pathname, authLoading, tenantLoading, user, analytics])
}
